import React, { useEffect, useState } from "react";
import api from "config/axiosConfig";
import GoogleSignin from "components/Auth/GoogleSignin";
import AuthService from "services/authService";
import { getAccountSettingsUiStrings, resolveAccountSettingsLocale } from "i18n/accountSettingsUi";
import ModalOrDrawer from "ui/ModalOrDrawer";
import InputField from "ui/InputField";
import { normalizeApiError } from "util/normalizeApiError";
import { apiRoutes } from "util/apiRoutes";

interface UserSettingsAccountGoogleConnectProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => Promise<void>;
}

const UserSettingsAccountGoogleConnect: React.FC<UserSettingsAccountGoogleConnectProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const ui = getAccountSettingsUiStrings(resolveAccountSettingsLocale());
    const [currentPassword, setCurrentPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setCurrentPassword("");
            setError(null);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const handleGoogleCode = async ({ code }: { code: string }) => {
        if (!currentPassword.trim()) {
            setError(ui.passwordRequiredError);
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await api.post(apiRoutes.auth.googleConnect, {
                code,
                currentPassword,
            });
            if (typeof response.data?.accessToken === "string" && response.data.accessToken.length > 0) {
                AuthService.setAccessToken(response.data.accessToken);
            }
            await onSuccess();
            onClose();
        } catch (requestError: unknown) {
            setError(normalizeApiError(requestError, ui.googleConnectError));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalOrDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={ui.googleConnectModalTitle}
            className="text-center"
        >
            <div className="flex flex-col gap-4 py-2">
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {ui.googleConnectModalDescription}
                </p>
                <InputField
                    type="password"
                    id="googleConnectPassword"
                    name="googleConnectPassword"
                    labelText={ui.googleConnectPasswordLabel}
                    autoComplete="current-password"
                    fieldRequired
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    error={error ?? undefined}
                    spellCheck={false}
                />
                <GoogleSignin
                    disabled={isSubmitting || currentPassword.trim().length === 0}
                    label={ui.googleConnectButtonLabel}
                    onCodeReceived={handleGoogleCode}
                    onError={() => setError(ui.googleConnectError)}
                />
                <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary w-full py-2 px-4"
                >
                    {ui.modalCancel}
                </button>
            </div>
        </ModalOrDrawer>
    );
};

export default UserSettingsAccountGoogleConnect;
